import React, { useEffect, useState } from "react";
import { api } from "../api";

// Newsletter is separate from security/account email and from future reminders.
export default function NewsletterPreferencesV1({ email, language = "en" }) {
  const fr = String(language).toLowerCase().startsWith("fr");
  const copy = fr ? {
    title: "Préférences e-mail", newsletter: "Newsletter NextScenes",
    desc: "Actualités de la plateforme et inspiration créative occasionnelles.",
    not_subscribed: "Non abonné(e)", pending: "Confirmation par e-mail en attente",
    subscribed: "Abonné(e)", unsubscribed: "Désabonné(e)",
    request: "Demander un e-mail de confirmation", cancel: "Se désabonner",
    consent: "Je souhaite recevoir la newsletter NextScenes (confirmation par e-mail requise).",
    pendingHelp: "Vérifiez votre boîte de réception pour confirmer votre abonnement.",
    loading: "Chargement des préférences…", refresh: "Actualiser",
    failed: "Les préférences e-mail sont temporairement indisponibles.",
    requested: "Demande reçue. Consultez votre messagerie si votre adresse est admissible.",
    removed: "Votre abonnement facultatif a été désactivé.",
  } : {
    title: "Email Preferences", newsletter: "NextScenes Newsletter",
    desc: "Occasional platform updates and creative inspiration.",
    not_subscribed: "Not subscribed", pending: "Email confirmation pending",
    subscribed: "Subscribed", unsubscribed: "Unsubscribed",
    request: "Request confirmation email", cancel: "Unsubscribe",
    consent: "I want to receive the NextScenes newsletter (email confirmation required).",
    pendingHelp: "Check your inbox to confirm your subscription.",
    loading: "Loading preferences…", refresh: "Refresh",
    failed: "Email preferences are temporarily unavailable.",
    requested: "Request received. Check your inbox if your address is eligible.",
    removed: "Your optional newsletter subscription has been turned off.",
  };
  const [status, setStatus] = useState("loading");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  async function refresh() {
    const result = await api.get("/api/newsletter/me");
    const next = result?.status;
    if (result?.ok !== true ||
        !["not_subscribed", "pending", "subscribed", "unsubscribed"].includes(next)) {
      throw new Error("Unable to load email preferences.");
    }
    setStatus(next);
  }

  useEffect(() => {
    let active = true;
    if (!email) return undefined;
    api.get("/api/newsletter/me").then((r) => {
      if (!active) return;
      const next = r?.status;
      setStatus(r?.ok === true && ["not_subscribed", "pending", "subscribed", "unsubscribed"].includes(next) ? next : "error");
    }).catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, [email]);

  async function subscribe() {
    if (!consent || busy || !email || !["not_subscribed","unsubscribed"].includes(status)) return;
    setBusy(true); setNotice(""); setError("");
    try {
      await api.post("/api/newsletter/me/subscribe", { consent: true, language: fr ? "fr" : "en" });
      await refresh();
      setConsent(false);
      setNotice(copy.requested);
    } catch {
      setError(copy.failed);
    } finally { setBusy(false); }
  }

  async function unsubscribe() {
    if (busy || !["pending","subscribed"].includes(status)) return;
    setBusy(true); setNotice(""); setError("");
    try {
      const result = await api.post("/api/newsletter/me/unsubscribe", {});
      if (result?.ok !== true) throw new Error("Failed to unsubscribe");
      await refresh();
      setConsent(false);
      setNotice(copy.removed);
    } catch {
      setError(copy.failed);
    } finally { setBusy(false); }
  }

  const canSubscribe = status === "not_subscribed" || status === "unsubscribed";
  const canUnsubscribe = status === "pending" || status === "subscribed";

  return (
    <section data-newsletter-profile-preferences-v1="true" aria-label={copy.title}
      style={{ marginTop: 12, padding: "16px 18px", border: "1px solid #d5e4d6",
        background: "#f7faf5", borderRadius: 13, color: "#244e38" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap" }}>
        <strong style={{ fontSize:16 }}>{copy.title}</strong>
        <button type="button" onClick={async()=>{setError("");setNotice("");setStatus("loading");try{await refresh();}catch{setStatus("error");}}}
          disabled={busy || status==="loading"} className="btn ns-btn-soft" style={{fontSize:12,padding:"6px 10px"}}>
          {copy.refresh}
        </button>
      </div>
      <div style={{ marginTop:12 }}>
        <strong>{copy.newsletter}</strong>
        <p style={{fontSize:13,lineHeight:1.5,margin:"4px 0 8px"}}>{copy.desc}</p>
        <div role="status" aria-live="polite" style={{fontSize:13,fontWeight:700}}>
          {status === "loading" ? copy.loading : status === "error" ? copy.failed : copy[status]}
        </div>
        {status === "pending" ? <p style={{fontSize:12,margin:"6px 0"}}>{copy.pendingHelp}</p> : null}
        {canSubscribe ? (
          <div style={{marginTop:10}}>
            <label style={{display:"flex",gap:9,alignItems:"flex-start",fontSize:13,lineHeight:1.5}}>
              <input type="checkbox" checked={consent} disabled={busy}
                onChange={e=>setConsent(Boolean(e.target.checked))} style={{marginTop:3}}/>
              <span>{copy.consent}</span>
            </label>
            <button type="button" className="btn ns-btn-soft" style={{marginTop:9}}
              disabled={!consent || busy} onClick={subscribe}>{copy.request}</button>
          </div>
        ) : null}
        {canUnsubscribe ? (
          <button type="button" className="btn ns-btn-soft" style={{marginTop:10}}
            disabled={busy} onClick={unsubscribe}>{copy.cancel}</button>
        ) : null}
        {notice ? <p role="status" style={{color:"#17643b",fontSize:13}}>{notice}</p> : null}
        {error ? <p role="alert" style={{color:"#a12b2b",fontSize:13}}>{error}</p> : null}
      </div>
    </section>
  );
}
